import React from "react";
import {
  Box,
  Chip,
  Divider,
  ListItem,
  ListItemButton,
  Stack,
} from "@mui/material";
import { Copyable } from "../../../copyable/Copyable";
import { useTranslation } from "../../../../hooks/useTranslation";
import { OrderOptimizedListResponse } from "../../../../pages/orders/utils/types";
import { InfoRow } from "./InfoRow";

type Props = {
  order: OrderOptimizedListResponse;
  isDevEnv: boolean;
  onSelect: () => void;
};

export const OrderResultItem: React.FC<Props> = ({
  order,
  isDevEnv,
  onSelect,
}) => {
  const { translateWithoutPrefix } = useTranslation("");

  const image = order.images?.[0];
  const asin = order.orderItems?.[0]?.asin;
  const supplyStatus = order.supplyOrders?.[0]?.supplyOrderStatus;

  const idLabel = translateWithoutPrefix("pages.orders.list.fields.orderId");
  const amazonLabel = translateWithoutPrefix(
    "pages.orders.list.fields.amazonOrderId",
  );
  const barcodeLabel = translateWithoutPrefix(
    "pages.orders.list.fields.barcode",
  );
  const statusLabel = translateWithoutPrefix(
    "pages.orders.list.fields.status.title",
  );
  const supplyStatusLabel = translateWithoutPrefix(
    "common.supplyOrderStatus.title",
  );
  const unknownText = translateWithoutPrefix("common.unknown");

  const amazonText = order.amazonOrderId ?? unknownText;
  const barcodeText = order.barcode ?? "-";
  const statusText =
    translateWithoutPrefix(
      "pages.orders.list.fields.status." + order.orderStatus,
    ) ?? "Unknown";
  const supplyStatusText =
    supplyStatus &&
    translateWithoutPrefix("common.supplyOrderStatus." + supplyStatus);

  const fallbackImage = isDevEnv ? "/icons/logo-dev.svg" : "/icons/logo.svg";
  const imageSrc = image || fallbackImage;

  return (
    <React.Fragment>
      <ListItem disablePadding>
        <ListItemButton
          onClick={onSelect}
          sx={{
            alignItems: "flex-start",
            gap: 1.5,
            py: 1.2,
            borderBottom: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <Box
            component="img"
            src={imageSrc}
            alt="order"
            sx={{
              width: 56,
              height: 56,
              objectFit: "contain",
              borderRadius: 1.5,
              flexShrink: 0,
              backgroundColor: "background.paper",
            }}
          />
          <Stack gap={1}>
            <InfoRow label={idLabel}>
              <Copyable size="small" initialLabel={order.id.toString()} />
            </InfoRow>

            {order.amazonOrderId && (
              <InfoRow label={amazonLabel}>
                <Copyable size="small" initialLabel={amazonText} />
              </InfoRow>
            )}

            {order.barcode && (
              <InfoRow label={barcodeLabel}>
                <Copyable size="small" initialLabel={barcodeText} />
              </InfoRow>
            )}

            {asin && (
              <InfoRow label="ASIN">
                <Copyable size="small" initialLabel={asin.toUpperCase()} />
              </InfoRow>
            )}

            <InfoRow label={statusLabel}>
              <Chip size="small" label={statusText} sx={{ fontSize: 11 }} />
            </InfoRow>

            {supplyStatusText && (
              <InfoRow label={supplyStatusLabel}>
                <Chip
                  size="small"
                  label={supplyStatusText}
                  sx={{ fontSize: 11 }}
                />
              </InfoRow>
            )}
          </Stack>
        </ListItemButton>
      </ListItem>
      <Divider sx={{ borderColor: "primary.main" }} />
    </React.Fragment>
  );
};
